/**
 * Custom Matrix Store
 *
 * Manages user-defined 2×2 prioritization matrix templates.
 *
 * Save scopes:
 *   'local'     — localStorage only; never leaves this browser; private to this user/device
 *   'workspace' — persisted to Supabase; visible to all members of the current workspace
 *   'org'       — persisted to Supabase; visible to all workspaces in the org/tenant
 */

import { create } from 'zustand';
import { getSafeSupabaseClient, generateUUID } from '../../lib/supabaseSync';

// ============================================================================
// Types
// ============================================================================

export type MatrixSaveScope = 'local' | 'workspace' | 'org';

export interface QuadrantDefinition {
  key: string;         // e.g. 'top-left'
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  name: string;        // e.g. 'Quick Wins'
  description: string; // e.g. 'High value, low cost'
  action: string;      // e.g. 'DO NOW'
  color: string;       // Tailwind bg token — e.g. 'bg-green-50'
  border: string;      // Tailwind border token — e.g. 'border-green-300'
  headerBg: string;    // Tailwind header bg token — e.g. 'bg-green-100'
}

export interface CustomMatrixTemplate {
  id: string;
  name: string;
  description?: string;

  xAxisLabel: string;
  xAxisLowLabel: string;
  xAxisHighLabel: string;
  yAxisLabel: string;
  yAxisLowLabel: string;
  yAxisHighLabel: string;

  quadrants: QuadrantDefinition[]; // always 4, in fixed position order

  scope: MatrixSaveScope;
  createdBy?: string;
  workspaceId?: string;
  orgId?: string;
  createdAt: Date;
  updatedAt: Date;
}

const LOCAL_STORAGE_KEY = 'peakPriorities_customMatrixTemplates';

// ============================================================================
// DB row ↔ domain model helpers
// ============================================================================

function rowToTemplate(row: Record<string, unknown>): CustomMatrixTemplate {
  return {
    id: row.id as string,
    name: row.name as string,
    description: row.description as string | undefined,
    xAxisLabel: row.x_axis_label as string,
    xAxisLowLabel: row.x_axis_low_label as string,
    xAxisHighLabel: row.x_axis_high_label as string,
    yAxisLabel: row.y_axis_label as string,
    yAxisLowLabel: row.y_axis_low_label as string,
    yAxisHighLabel: row.y_axis_high_label as string,
    quadrants: row.quadrants as QuadrantDefinition[],
    scope: row.scope as MatrixSaveScope,
    createdBy: row.created_by as string | undefined,
    workspaceId: row.workspace_id as string | undefined,
    orgId: row.org_id as string | undefined,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
  };
}

function templateToRow(t: CustomMatrixTemplate): Record<string, unknown> {
  return {
    id: t.id,
    name: t.name,
    description: t.description ?? null,
    x_axis_label: t.xAxisLabel,
    x_axis_low_label: t.xAxisLowLabel,
    x_axis_high_label: t.xAxisHighLabel,
    y_axis_label: t.yAxisLabel,
    y_axis_low_label: t.yAxisLowLabel,
    y_axis_high_label: t.yAxisHighLabel,
    quadrants: t.quadrants,
    scope: t.scope,
    created_by: t.createdBy ?? null,
    workspace_id: t.workspaceId ?? null,
    org_id: t.orgId ?? null,
  };
}

// ============================================================================
// localStorage helpers
// ============================================================================

function loadLocalTemplates(): CustomMatrixTemplate[] {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CustomMatrixTemplate[];
    return parsed.map(t => ({
      ...t,
      createdAt: new Date(t.createdAt),
      updatedAt: new Date(t.updatedAt),
    }));
  } catch {
    return [];
  }
}

function saveLocalTemplates(templates: CustomMatrixTemplate[]): void {
  const local = templates.filter(t => t.scope === 'local');
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(local));
}

// ============================================================================
// Default quadrant presets
// ============================================================================

export const DEFAULT_QUADRANT_COLORS: Array<{
  color: string;
  border: string;
  headerBg: string;
}> = [
  { color: 'bg-green-50',  border: 'border-green-300',  headerBg: 'bg-green-100'  }, // top-right (high/high)
  { color: 'bg-blue-50',   border: 'border-blue-300',   headerBg: 'bg-blue-100'   }, // top-left  (high/low)
  { color: 'bg-yellow-50', border: 'border-yellow-300', headerBg: 'bg-yellow-100' }, // bottom-right (low/high)
  { color: 'bg-gray-50',   border: 'border-gray-300',   headerBg: 'bg-gray-100'   }, // bottom-left (low/low)
];

export function buildDefaultQuadrants(
  xHighLabel: string,
  xLowLabel: string,
  yHighLabel: string,
  yLowLabel: string,
): QuadrantDefinition[] {
  return [
    {
      key: 'top-left',
      position: 'top-left',
      name: `${yHighLabel} / ${xLowLabel}`,
      description: `High ${yHighLabel.toLowerCase()}, low ${xLowLabel.toLowerCase()}`,
      action: 'SCHEDULE',
      ...DEFAULT_QUADRANT_COLORS[1],
    },
    {
      key: 'top-right',
      position: 'top-right',
      name: `${yHighLabel} / ${xHighLabel}`,
      description: `High ${yHighLabel.toLowerCase()}, high ${xHighLabel.toLowerCase()}`,
      action: 'DO NOW',
      ...DEFAULT_QUADRANT_COLORS[0],
    },
    {
      key: 'bottom-left',
      position: 'bottom-left',
      name: `${yLowLabel} / ${xLowLabel}`,
      description: `Low ${yLowLabel.toLowerCase()}, low ${xLowLabel.toLowerCase()}`,
      action: 'SKIP',
      ...DEFAULT_QUADRANT_COLORS[3],
    },
    {
      key: 'bottom-right',
      position: 'bottom-right',
      name: `${yLowLabel} / ${xHighLabel}`,
      description: `Low ${yLowLabel.toLowerCase()}, high ${xHighLabel.toLowerCase()}`,
      action: 'DELEGATE',
      ...DEFAULT_QUADRANT_COLORS[2],
    },
  ];
}

// ============================================================================
// Store
// ============================================================================

interface CustomMatrixStoreState {
  templates: CustomMatrixTemplate[];
  loading: boolean;
  error: string | null;

  /** Load templates: local from localStorage + remote from Supabase filtered by workspaceId/orgId */
  fetchTemplates: (workspaceId?: string, orgId?: string) => Promise<void>;

  /** Create and persist a new template */
  createTemplate: (
    template: Omit<CustomMatrixTemplate, 'id' | 'createdAt' | 'updatedAt'>,
  ) => Promise<CustomMatrixTemplate>;

  /** Update an existing template */
  updateTemplate: (
    id: string,
    updates: Partial<Omit<CustomMatrixTemplate, 'id' | 'createdAt'>>,
  ) => Promise<void>;

  /** Delete a template */
  deleteTemplate: (id: string) => Promise<void>;
}

export const useCustomMatrixStore = create<CustomMatrixStoreState>((set, get) => ({
  templates: [],
  loading: false,
  error: null,

  fetchTemplates: async (workspaceId?: string, orgId?: string) => {
    set({ loading: true, error: null });

    // Always load local templates from localStorage
    const localTemplates = loadLocalTemplates();

    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      set({ templates: localTemplates, loading: false });
      return;
    }

    try {
      let query = supabase
        .from('custom_matrix_templates')
        .select('*')
        .order('created_at', { ascending: false });

      // Build OR filter: workspace scope matching current workspace OR org scope matching current org
      const orParts: string[] = [];
      if (workspaceId) {
        orParts.push(`and(scope.eq.workspace,workspace_id.eq.${workspaceId})`);
      }
      if (orgId) {
        orParts.push(`and(scope.eq.org,org_id.eq.${orgId})`);
      }

      if (orParts.length > 0) {
        query = query.or(orParts.join(','));
      } else {
        // No context provided — return only local templates
        set({ templates: localTemplates, loading: false });
        return;
      }

      const { data, error } = await query;

      if (error) {
        console.error('Failed to fetch custom matrix templates:', error);
        set({ templates: localTemplates, loading: false, error: error.message });
        return;
      }

      const remoteTemplates = (data ?? []).map(rowToTemplate);
      set({ templates: [...localTemplates, ...remoteTemplates], loading: false });
    } catch (err) {
      console.error('Unexpected error fetching custom matrix templates:', err);
      set({ templates: localTemplates, loading: false });
    }
  },

  createTemplate: async (templateData) => {
    const now = new Date();
    const newTemplate: CustomMatrixTemplate = {
      ...templateData,
      id: generateUUID(),
      createdAt: now,
      updatedAt: now,
    };

    // Optimistic update
    set(state => ({ templates: [newTemplate, ...state.templates] }));

    if (newTemplate.scope === 'local') {
      // Persist to localStorage only
      const current = get().templates;
      saveLocalTemplates(current);
      return newTemplate;
    }

    // Persist to Supabase
    const supabase = getSafeSupabaseClient();
    if (supabase) {
      const { error } = await supabase
        .from('custom_matrix_templates')
        .insert(templateToRow(newTemplate));

      if (error) {
        // Rollback optimistic update
        set(state => ({ templates: state.templates.filter(t => t.id !== newTemplate.id) }));
        throw new Error(`Failed to save template: ${error.message}`);
      }
    }

    return newTemplate;
  },

  updateTemplate: async (id, updates) => {
    const existing = get().templates.find(t => t.id === id);
    if (!existing) return;

    const updated: CustomMatrixTemplate = {
      ...existing,
      ...updates,
      updatedAt: new Date(),
    };

    set(state => ({
      templates: state.templates.map(t => (t.id === id ? updated : t)),
    }));

    if (updated.scope === 'local') {
      saveLocalTemplates(get().templates);
      return;
    }

    const supabase = getSafeSupabaseClient();
    if (supabase) {
      const { error } = await supabase
        .from('custom_matrix_templates')
        .update(templateToRow(updated))
        .eq('id', id);

      if (error) {
        // Rollback
        set(state => ({
          templates: state.templates.map(t => (t.id === id ? existing : t)),
        }));
        throw new Error(`Failed to update template: ${error.message}`);
      }
    }
  },

  deleteTemplate: async (id) => {
    const existing = get().templates.find(t => t.id === id);
    if (!existing) return;

    set(state => ({ templates: state.templates.filter(t => t.id !== id) }));

    if (existing.scope === 'local') {
      saveLocalTemplates(get().templates);
      return;
    }

    const supabase = getSafeSupabaseClient();
    if (supabase) {
      const { error } = await supabase
        .from('custom_matrix_templates')
        .delete()
        .eq('id', id);

      if (error) {
        // Rollback
        set(state => ({ templates: [existing, ...state.templates] }));
        throw new Error(`Failed to delete template: ${error.message}`);
      }
    }
  },
}));
